import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { StrikerBrand } from '@striker/ui-shell';

type CatalogCategory = {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  module_count: number;
};

type FormState = {
  category_code: string;
  business_name: string;
  slug: string;
  owner_email: string;
  owner_password: string;
  owner_display_name: string;
  owner_phone: string;
  vat_number: string;
};

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const completingRef = useRef(false);

  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    category_code: '',
    business_name: '',
    slug: '',
    owner_email: '',
    owner_password: '',
    owner_display_name: '',
    owner_phone: '',
    vat_number: '',
  });

  // Load categories on mount + read category from URL
  useEffect(() => {
    let cancelled = false;
    const catFromUrl = String(searchParams.get('category') || '').trim().toLowerCase();
    if (catFromUrl) {
      setForm((prev) => (prev.category_code ? prev : { ...prev, category_code: catFromUrl }));
      setStep(1);
    }
    void fetch('/api/v1/auth/catalog/categories')
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as { categories?: CatalogCategory[] };
        if (!cancelled && res.ok && Array.isArray(body.categories)) {
          setCategories(body.categories);
        }
      })
      .catch(() => {
        /* categories are optional — fallback to no filter */
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    const checkout = String(searchParams.get('checkout') || '');
    const sessionId = String(searchParams.get('session_id') || '');
    if (checkout === 'cancel') {
      setError('Pagamento annullato. Puoi riprovare quando vuoi.');
      return;
    }
    if (checkout !== 'success' || !sessionId || completingRef.current) return;
    completingRef.current = true;
    setBusy(true);
    setError(null);
    void fetch('/api/v1/auth/register/checkout-complete', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
          error?: string;
          ownerEmail?: string;
          slug?: string;
          tenantId?: number;
        };
        if (!res.ok) throw new Error(body.message || body.error || `Completamento fallito (${res.status})`);
        try {
          sessionStorage.setItem(
            'striker_registration_welcome',
            JSON.stringify({
              ownerEmail: body.ownerEmail,
              slug: body.slug,
              tenantId: body.tenantId,
            }),
          );
        } catch {
          /* ignore */
        }
        navigate('/tenant/home', { replace: true });
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        completingRef.current = false;
      })
      .finally(() => setBusy(false));
  }, [searchParams, navigate]);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: FormEvent, overrideCategoryCode?: string) {
    event.preventDefault();
    const categoryCode = overrideCategoryCode ?? form.category_code;
    if (!categoryCode) {
      setError('Seleziona una categoria');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/auth/billing/checkout-session', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          category_code: categoryCode,
          business_name: form.business_name,
          slug: form.slug || undefined,
          owner_email: form.owner_email,
          owner_password: form.owner_password,
          owner_display_name: form.owner_display_name || undefined,
          owner_phone: form.owner_phone || undefined,
          vat_number: form.vat_number || undefined,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        checkoutUrl?: string;
        message?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `Checkout fallito (${res.status})`);
      if (!body.checkoutUrl) throw new Error('URL Checkout mancante');
      window.location.assign(body.checkoutUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="sk-shift sk-register">
      <div className="sk-shift__glow" aria-hidden="true" />
      <header className="sk-shift__nav">
        <Link to="/" className="sk-shift__nav-brand" aria-label="StrikerPos">
          <StrikerBrand surface="dark" title="StrikerPos" />
        </Link>
        <Link className="sk-shift__nav-cta" to="/login">
          Accedi
        </Link>
      </header>

      <main className="sk-register__main">
        <p className="sk-shift__eyebrow">Iscrizione</p>
        <h1 className="sk-shift__section-title">Apri il tuo locale su StrikerPos</h1>
        <p className="sk-shift__section-lead">
          Inserisci i dati fiscali, scegli la categoria e conferma la carta su Stripe.
          Iscrizione gratuita — acquisti i moduli che ti servono dallo store.
        </p>

        {error ? <p className="sk-shift__status sk-shift__status--err">{error}</p> : null}
        {busy ? <p className="sk-shift__status">Elaborazione…</p> : null}

        {/* Step indicator (blueprint: 5 step totali, 2 in registrazione) */}
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem' }}>
          {[0, 1].map((s) => (
            <div key={s} style={{
              flex: 1, height: '4px', borderRadius: '2px',
              background: step >= s ? '#3b82f6' : 'var(--sk-border)',
              transition: 'background 0.2s',
            }} />
          ))}
        </div>

        {/* Step 1: Dati fiscali (blueprint: step separato, prima della categoria) */}
        {step === 0 && (
          <form
            className="sk-register__form"
            onSubmit={(e) => { e.preventDefault(); setStep(1); }}
          >
            <p className="sk-register__step-label">Step 1 di 2 — Dati fiscali</p>

            <label className="sk-field">
              <span>Nome attività</span>
              <input
                className="sk-input"
                required
                value={form.business_name}
                onChange={(e) => patch('business_name', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>P. IVA</span>
              <input
                className="sk-input"
                required
                placeholder="es. 01234567890"
                value={form.vat_number}
                onChange={(e) => patch('vat_number', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Email owner</span>
              <input
                className="sk-input"
                type="email"
                required
                autoComplete="username"
                value={form.owner_email}
                onChange={(e) => patch('owner_email', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Password owner</span>
              <input
                className="sk-input"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={form.owner_password}
                onChange={(e) => patch('owner_password', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Nome visualizzato</span>
              <input
                className="sk-input"
                value={form.owner_display_name}
                onChange={(e) => patch('owner_display_name', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Telefono</span>
              <input
                className="sk-input"
                value={form.owner_phone}
                onChange={(e) => patch('owner_phone', e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Slug pubblico (opzionale)</span>
              <input
                className="sk-input"
                placeholder="es. due-magnolie"
                value={form.slug}
                onChange={(e) => patch('slug', e.target.value)}
              />
            </label>

            <button type="submit" className="sk-shift__btn sk-shift__btn--primary">
              Avanti → Scegli categoria
            </button>
            <p className="sk-register__fine">
              <Link to="/">← Home</Link>
            </p>
          </form>
        )}

        {/* Step 2: Categoria (blueprint: step 2) */}
        {step === 1 && (
          <div className="sk-register__categories">
            <p className="sk-register__step-label">
              Step 2 di 2 — Scegli la tua attività{' '}
              <button type="button" className="sk-register__linkish" onClick={() => setStep(0)}>
                · Torna ai dati
              </button>
            </p>
            <div className="sk-register__cat-grid">
              {categories.length === 0 ? (
                <p className="sk-shift__status">Caricamento categorie…</p>
              ) : null}
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`sk-register__cat${form.category_code === cat.code ? ' sk-register__cat--active' : ''}`}
                  onClick={() => {
                    patch('category_code', cat.code);
                    // Auto-submit when category is selected (pass code directly
                    // because setForm is async and onSubmit would read stale state)
                    void onSubmit({ preventDefault: () => {} } as FormEvent, cat.code);
                  }}
                >
                  <span className="sk-register__cat-name">{cat.name}</span>
                  <span className="sk-register__cat-meta">{cat.module_count} moduli</span>
                </button>
              ))}
            </div>
            {form.category_code && (
              <button
                type="button"
                className="sk-shift__btn sk-shift__btn--primary"
                style={{ marginTop: '1rem' }}
                onClick={() => void onSubmit({ preventDefault: () => {} } as FormEvent)}
                disabled={busy}
              >
                {busy ? 'Reindirizzamento…' : 'Continua su Stripe'}
              </button>
            )}
            <p className="sk-register__fine">
              <Link to="/">← Home</Link>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
